// components/GoogleReviewsCarousel.jsx
import React, { useState, useEffect, useRef } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Star, ChevronLeft, ChevronRight, Loader2 } from 'lucide-react';

const GoogleReviewsCarousel = () => {
  const [reviews, setReviews] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [currentIndex, setCurrentIndex] = useState(0);
  const [isAutoPlaying, setIsAutoPlaying] = useState(true);
  const intervalRef = useRef(null);

  // Static fallback reviews
  const staticReviews = [
    {
      name: 'Sarah Johnson',
      location: 'Budd Lake, NJ',
      rating: 5,
      text: 'Mount Olive Plumbing saved the day when our water heater failed. Professional, quick, and reasonably priced!',
      date: 'a week ago'
    },
    {
      name: 'Mike Peterson',
      location: 'Hackettstown, NJ',
      rating: 5,
      text: 'Excellent HVAC installation. The team was professional and the work quality exceeded expectations.',
      date: '2 weeks ago'
    },
    {
      name: 'Lisa Chen',
      location: 'Mount Olive, NJ',
      rating: 5,
      text: 'Emergency plumbing service at 2 AM - they were there within an hour. Highly recommend!',
      date: '3 weeks ago'
    },
    {
      name: 'Robert Williams',
      location: 'Flanders, NJ',
      rating: 5,
      text: 'Best HVAC service in the area! Fair pricing and exceptional customer service. They fixed our AC unit quickly.',
      date: 'a month ago'
    },
    {
      name: 'Jennifer Davis',
      location: 'Chester, NJ',
      rating: 5,
      text: 'Very professional team. They installed our new heating system efficiently and cleaned up after themselves.',
      date: 'a month ago'
    },
    {
      name: 'David Thompson',
      location: 'Long Valley, NJ',
      rating: 5,
      text: 'Prompt response to our plumbing emergency. The technician was knowledgeable and fixed the issue quickly.',
      date: '2 months ago'
    }
  ];

  useEffect(() => {
    fetchGoogleReviews();
  }, []);

  // Auto-play carousel
  useEffect(() => {
    if (isAutoPlaying && reviews.length > 0) {
      intervalRef.current = setInterval(() => {
        handleNext();
      }, 5000); // Change slide every 5 seconds

      return () => {
        if (intervalRef.current) {
          clearInterval(intervalRef.current);
        }
      };
    }
  }, [currentIndex, isAutoPlaying, reviews.length]);

  const fetchGoogleReviews = async () => {
    setLoading(true);
    setError(null);

    // Check localStorage cache first
    const cached = localStorage.getItem('google-reviews-cache');
    if (cached) {
      try {
        const { reviews: cachedReviews, timestamp } = JSON.parse(cached);
        const hoursSinceCache = (Date.now() - timestamp) / (1000 * 60 * 60);
        
        if (hoursSinceCache < 24 && cachedReviews.length > 0) {
          setReviews(cachedReviews);
          setLoading(false);
          return;
        }
      } catch (e) {
        console.log('Cache parse error:', e);
      }
    }

    // Try fetching with CORS proxy
    const proxies = [
      'https://api.allorigins.win/raw?url=',
      'https://corsproxy.io/?',
      'https://proxy.cors.sh/'
    ];

    let fetchedReviews = [];
    
    for (const proxy of proxies) {
      try {
        // Using the Google Maps place URL
        const googleUrl = 'https://www.google.com/maps/place/Mount+Olive+Plumbing+Heating+and+Air+Conditioning/@40.9106645,-74.5956785,11z/data=!4m8!3m7!1s0x8d905a7172a10d91:0x14634a2cb5b330f8';
        
        const response = await fetch(proxy + encodeURIComponent(googleUrl), {
          method: 'GET',
          headers: {
            'Accept': 'text/html,application/xhtml+xml'
          }
        });

        if (response.ok) {
          const html = await response.text();
          fetchedReviews = parseReviewsFromHTML(html);
          
          if (fetchedReviews.length > 0) {
            // Cache the results
            localStorage.setItem('google-reviews-cache', JSON.stringify({
              reviews: fetchedReviews,
              timestamp: Date.now()
            }));
            break;
          }
        }
      } catch (error) {
        console.log(`Proxy ${proxy} failed:`, error);
        continue;
      }
    }

    // Use fetched reviews or fall back to static
    if (fetchedReviews.length > 0) {
      setReviews(fetchedReviews);
    } else {
      console.log('Using static reviews as fallback');
      setReviews(staticReviews);
    }
    
    setLoading(false);
  };

  const parseReviewsFromHTML = (html) => {
    const reviews = [];
    
    try {
      // Try to find reviews in various formats
      // Method 1: Look for structured data
      const jsonLdRegex = /<script type="application\/ld\+json">(.*?)<\/script>/gs;
      const matches = html.matchAll(jsonLdRegex);
      
      for (const match of matches) {
        try {
          const data = JSON.parse(match[1]);
          if (data['@type'] === 'LocalBusiness' && data.review) {
            data.review.forEach(r => {
              if (r.reviewRating?.ratingValue >= 4) {
                reviews.push({
                  name: r.author?.name || 'Google User',
                  location: 'Morris County, NJ',
                  rating: parseInt(r.reviewRating.ratingValue),
                  text: r.reviewBody || r.description,
                  date: r.datePublished || 'Recently'
                });
              }
            });
          }
        } catch (e) {
          // Continue to next match
        }
      }

      // Method 2: Look for review patterns in HTML
      if (reviews.length === 0) {
        // Common patterns in Google's HTML (these change frequently)
        const reviewPatterns = [
          /data-review-id="[^"]*"[^>]*>(.*?)<\/div>/gs,
          /class="[^"]*review[^"]*"[^>]*>(.*?)<\/div>/gs
        ];

        for (const pattern of reviewPatterns) {
          const reviewMatches = html.matchAll(pattern);
          for (const match of reviewMatches) {
            const reviewHtml = match[1];
            // Try to extract review details
            const nameMatch = reviewHtml.match(/(?:aria-label="|data-reviewer-name="|class="[^"]*name[^"]*">)([^"<]+)/);
            const textMatch = reviewHtml.match(/(?:data-review-text="|class="[^"]*text[^"]*">)([^"<]+)/);
            const ratingMatch = reviewHtml.match(/(?:aria-label="|data-rating=")(\d+)/);
            
            if (nameMatch && textMatch) {
              reviews.push({
                name: nameMatch[1],
                location: 'Morris County, NJ',
                rating: ratingMatch ? parseInt(ratingMatch[1]) : 5,
                text: textMatch[1],
                date: 'Recently'
              });
            }
          }
        }
      }
    } catch (error) {
      console.error('Error parsing reviews:', error);
    }

    // Return only 5-star reviews, limited to 10
    return reviews.filter(r => r.rating === 5).slice(0, 10);
  };

  const handlePrevious = () => {
    setCurrentIndex((prevIndex) => 
      prevIndex === 0 ? reviews.length - 3 : prevIndex - 1
    );
  };

  const handleNext = () => {
    setCurrentIndex((prevIndex) => 
      prevIndex >= reviews.length - 3 ? 0 : prevIndex + 1
    );
  };

  const handleDotClick = (index) => {
    setCurrentIndex(index);
    setIsAutoPlaying(false);
    // Resume auto-play after 10 seconds
    setTimeout(() => setIsAutoPlaying(true), 10000);
  };

  const getVisibleReviews = () => {
    if (reviews.length <= 3) return reviews;
    
    const visible = [];
    for (let i = 0; i < 3; i++) {
      const index = (currentIndex + i) % reviews.length;
      visible.push(reviews[index]);
    }
    return visible;
  };

  if (loading) {
    return (
      <section className="py-20 bg-gradient-to-br from-blue-50 to-gray-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center">
            <Loader2 className="h-12 w-12 animate-spin mx-auto text-blue-600 mb-4" />
            <p className="text-gray-600">Loading customer reviews...</p>
          </div>
        </div>
      </section>
    );
  }

  const visibleReviews = getVisibleReviews();
  const totalDots = Math.max(1, reviews.length - 2);

  return (
    <section className="py-20 bg-gradient-to-br from-blue-50 to-gray-50 overflow-hidden">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <motion.div
          className="text-center mb-16"
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
        >
          <h2 className="text-4xl lg:text-5xl font-bold text-gray-900 mb-6">
            What Our Customers Say
          </h2>
          <p className="text-xl text-gray-600">
            Don't just take our word for it - hear from satisfied customers across Morris County
          </p>
        </motion.div>

        <div className="relative">
          {/* Carousel Container */}
          <div className="relative">
            {/* Previous Button */}
            {reviews.length > 3 && (
              <button
                onClick={() => {
                  handlePrevious();
                  setIsAutoPlaying(false);
                  setTimeout(() => setIsAutoPlaying(true), 10000);
                }}
                className="absolute left-0 top-1/2 -translate-y-1/2 -translate-x-4 z-10 bg-white rounded-full p-2 shadow-lg hover:shadow-xl transition-all duration-300 hover:scale-110"
                aria-label="Previous reviews"
              >
                <ChevronLeft className="h-6 w-6 text-gray-700" />
              </button>
            )}

            {/* Reviews Grid */}
            <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
              <AnimatePresence mode="wait">
                {visibleReviews.map((testimonial, index) => (
                  <motion.div
                    key={`${currentIndex}-${index}`}
                    className="bg-white p-8 rounded-3xl shadow-lg hover:shadow-xl transition-all duration-300"
                    initial={{ opacity: 0, x: 100 }}
                    animate={{ opacity: 1, x: 0 }}
                    exit={{ opacity: 0, x: -100 }}
                    transition={{ 
                      duration: 0.5, 
                      delay: index * 0.1,
                      ease: "easeInOut"
                    }}
                    whileHover={{ y: -5 }}
                  >
                    <div className="flex space-x-1 mb-4">
                      {[...Array(testimonial.rating)].map((_, i) => (
                        <Star key={i} className="h-5 w-5 text-yellow-400 fill-current" />
                      ))}
                    </div>
                    <p className="text-gray-700 leading-relaxed mb-6 italic line-clamp-4">
                      "{testimonial.text}"
                    </p>
                    <div>
                      <div className="font-semibold text-gray-900">{testimonial.name}</div>
                      <div className="text-gray-500 text-sm">{testimonial.location}</div>
                      <div className="text-gray-400 text-xs mt-1">{testimonial.date}</div>
                    </div>
                  </motion.div>
                ))}
              </AnimatePresence>
            </div>

            {/* Next Button */}
            {reviews.length > 3 && (
              <button
                onClick={() => {
                  handleNext();
                  setIsAutoPlaying(false);
                  setTimeout(() => setIsAutoPlaying(true), 10000);
                }}
                className="absolute right-0 top-1/2 -translate-y-1/2 translate-x-4 z-10 bg-white rounded-full p-2 shadow-lg hover:shadow-xl transition-all duration-300 hover:scale-110"
                aria-label="Next reviews"
              >
                <ChevronRight className="h-6 w-6 text-gray-700" />
              </button>
            )}
          </div>

          {/* Dots Indicator */}
          {reviews.length > 3 && (
            <div className="flex justify-center mt-8 space-x-2">
              {[...Array(totalDots)].map((_, index) => (
                <button
                  key={index}
                  onClick={() => handleDotClick(index)}
                  className={`h-2 transition-all duration-300 ${
                    index === currentIndex 
                      ? 'w-8 bg-blue-600' 
                      : 'w-2 bg-gray-300 hover:bg-gray-400'
                  } rounded-full`}
                  aria-label={`Go to review set ${index + 1}`}
                />
              ))}
            </div>
          )}
        </div>

        {/* Leave a Review Button */}
        <motion.div
          className="text-center mt-12"
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ delay: 0.3 }}
        >
          <a
            href="https://www.google.com/maps/place//data=!4m3!3m2!1s0x8d905a7172a10d91:0x14634a2cb5b330f8!12e1?source=g.page.m._&laa=merchant-review-solicitation"
            target="_blank"
            rel="noopener noreferrer"
            className="inline-flex items-center gap-3 bg-blue-600 hover:bg-blue-700 text-white font-bold text-lg px-8 py-4 rounded-full transition-all duration-300 hover:shadow-xl transform hover:-translate-y-1"
          >
            {/* Google Icon SVG */}
            <svg className="w-6 h-6" viewBox="0 0 24 24" fill="currentColor">
              <path d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"/>
              <path d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"/>
              <path d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"/>
              <path d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"/>
            </svg>
            Leave a Review
          </a>
        </motion.div>

        {/* Error Message (if needed) */}
        {error && (
          <div className="mt-4 text-center text-sm text-gray-500">
            {error}
          </div>
        )}
      </div>
    </section>
  );
};

export default GoogleReviewsCarousel;